import { useState, useEffect, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Target, Mail, Phone, Building2, RefreshCw, Search,
  Plus, ChevronRight, User, MessageSquare, X, Save,
  LayoutGrid, List, Send, Lock, AlertTriangle, Reply,
  Settings, Trash2, FileText
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

const DEFAULT_STATUSES = [
  { id: "new",        label: "New",        tone: "bg-blue-100 text-blue-700 border-blue-200",       headerTone: "bg-blue-50 border-b-blue-200 text-blue-800",       color: "blue" },
  { id: "contacted",  label: "Contacted",  tone: "bg-violet-100 text-violet-700 border-violet-200",   headerTone: "bg-violet-50 border-b-violet-200 text-violet-800",   color: "violet" },
  { id: "qualified",  label: "Qualified",  tone: "bg-amber-100 text-amber-700 border-amber-200",     headerTone: "bg-amber-50 border-b-amber-200 text-amber-800",     color: "amber" },
  { id: "won",        label: "Won",        tone: "bg-emerald-100 text-emerald-700 border-emerald-200", headerTone: "bg-emerald-50 border-b-emerald-200 text-emerald-800", color: "emerald" },
  { id: "lost",       label: "Lost",       tone: "bg-gray-100 text-gray-700 border-gray-200",        headerTone: "bg-gray-50 border-b-gray-200 text-gray-700",       color: "gray" },
];

// Tailwind-class lookup for custom pipeline columns. Listing all combos
// explicitly so Tailwind's JIT picks them up at build time.
const COLOR_TONES = {
  blue:    { tone: "bg-blue-100 text-blue-700 border-blue-200",       header: "bg-blue-50 border-b-blue-200 text-blue-800" },
  violet:  { tone: "bg-violet-100 text-violet-700 border-violet-200",   header: "bg-violet-50 border-b-violet-200 text-violet-800" },
  amber:   { tone: "bg-amber-100 text-amber-700 border-amber-200",     header: "bg-amber-50 border-b-amber-200 text-amber-800" },
  emerald: { tone: "bg-emerald-100 text-emerald-700 border-emerald-200", header: "bg-emerald-50 border-b-emerald-200 text-emerald-800" },
  red:     { tone: "bg-red-100 text-red-700 border-red-200",           header: "bg-red-50 border-b-red-200 text-red-800" },
  gray:    { tone: "bg-gray-100 text-gray-700 border-gray-200",        header: "bg-gray-50 border-b-gray-200 text-gray-700" },
};

const SOURCE_LABEL = {
  contact_form:               "Website Contact",
  demo_request:               "Demo Request",
  signup_abandoned:           "Abandoned Signup",
  trial_expired_no_upgrade:   "Trial Expired",
  plan_cancelled:             "Plan Cancelled",
  plan_expired:               "Plan Expired",
  manual:                     "Manual",
};

const formatCurrency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : "—";
const formatDateTime = (s) => s ? new Date(s).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : "—";

export default function BackofficeLeads() {
  const { theme } = useOutletContext();
  const isDark = theme === "dark";

  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [pipelineValue, setPipelineValue] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  // 'kanban' = Jira-style drag-drop board, 'table' = legacy compact list.
  const [view, setView] = useState("kanban");

  // Pipeline + email templates loaded from backend so super-admin can
  // customise them without a code change. We hydrate STATUSES from the
  // pipeline config on mount, falling back to the hardcoded defaults.
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [templates, setTemplates] = useState([]);
  const [availableVars, setAvailableVars] = useState([]);

  // Pipeline editor dialog
  const [showPipelineEditor, setShowPipelineEditor] = useState(false);
  const [pipelineDraft, setPipelineDraft] = useState([]);
  const [pipelineSaving, setPipelineSaving] = useState(false);

  // Templates editor dialog
  const [showTemplatesEditor, setShowTemplatesEditor] = useState(false);
  const [tplDraft, setTplDraft] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "", email: "", company_name: "", phone: "",
    source: "manual", notes: "", estimated_value: 0,
  });
  const [creating, setCreating] = useState(false);

  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Drag state for Kanban — we just remember which lead is being dragged so
  // dropping it onto a column can call PATCH /leads/{id} {status}.
  const draggingRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads/`, { withCredentials: true });
      setLeads(r.data?.leads || []);
      setPipeline(r.data?.pipeline || {});
      setPipelineValue(r.data?.pipeline_value || {});
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  // Pipeline + templates one-shot loader. Re-runs whenever the pipeline or
  // templates editor saves.
  const fetchSettings = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API}/leads/config/pipeline`, { withCredentials: true }),
        axios.get(`${API}/leads/templates/`, { withCredentials: true }),
      ]);
      const cols = (p.data?.columns || []).map(c => {
        const colorTones = COLOR_TONES[c.color] || COLOR_TONES.violet;
        return { id: c.id, label: c.label, color: c.color, order: c.order, tone: colorTones.tone, headerTone: colorTones.header };
      });
      if (cols.length > 0) setStatuses(cols);
      setTemplates(t.data?.templates || []);
      setAvailableVars(t.data?.available_variables || []);
    } catch (e) {
      // Settings endpoint missing or unauthorized — just keep defaults.
    }
  }, []);

  useEffect(() => { fetchLeads(); fetchSettings(); }, [fetchLeads, fetchSettings]);

  const visible = leads.filter(l => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.name||"").toLowerCase().includes(q)
        || (l.email||"").toLowerCase().includes(q)
        || (l.company_name||"").toLowerCase().includes(q);
  });

  const submitCreate = async () => {
    if (!createForm.email) { toast.error("Email is required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/leads/`, createForm, { withCredentials: true });
      toast.success("Lead created");
      setShowCreate(false);
      setCreateForm({ name:"", email:"", company_name:"", phone:"", source:"manual", notes:"", estimated_value:0 });
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create lead");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setEditForm({
      status: lead.status,
      assigned_to: lead.assigned_to || "",
      notes: lead.notes || "",
      estimated_value: lead.estimated_value || 0,
      // Note composer
      noteBody: "",
      noteSubject: "",
      noteVisible: false,
      // Email composer
      emailSubject: "",
      emailBody: "",
    });
  };

  // ---- Variable substitution helpers ---------------------------------------
  // Build the lookup for {{var}} replacement based on the current lead. The
  // sender-side variables (`my_name`, `my_email`) are pulled from the JWT-
  // decoded super-admin (best effort) so signoffs are personalised.
  const senderInfo = (() => {
    try {
      const s = window.localStorage.getItem('user');
      if (s) { const u = JSON.parse(s); return { my_name: u.name || u.email, my_email: u.email || '' }; }
    } catch { /* ignore */ }
    return { my_name: '', my_email: '' };
  })();
  const buildVarMap = (lead) => {
    if (!lead) return {};
    const fname = (lead.name || '').split(' ')[0] || '';
    return {
      name: lead.name || '',
      first_name: fname,
      email: lead.email || '',
      company_name: lead.company_name || '',
      phone: lead.phone || '',
      plan_name: lead.plan_id || '',
      paid_price: lead.estimated_value ? `£${Number(lead.estimated_value).toFixed(2)}` : '',
      renewal_date: lead.metadata?.renewal_date || '',
      trial_end_date: lead.metadata?.trial_end_date || '',
      customer_name: lead.name || '',
      customer_id: lead.metadata?.customer_id || '',
      ...senderInfo,
    };
  };
  const substituteVars = (text, varMap) => {
    if (!text) return text;
    return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => varMap[k] !== undefined ? varMap[k] : `{{${k}}}`);
  };

  // ---- Pipeline editor save ------------------------------------------------
  const savePipeline = async () => {
    const cleaned = pipelineDraft
      .map((c, idx) => ({
        id: (c.id || c.label || '').trim().toLowerCase().replace(/\s+/g, '_'),
        label: (c.label || '').trim(),
        color: c.color || 'violet',
        order: idx,
      }))
      .filter(c => c.id && c.label);
    if (cleaned.length === 0) { toast.error("Add at least one column"); return; }
    setPipelineSaving(true);
    try {
      await axios.put(`${API}/leads/config/pipeline`, cleaned, { withCredentials: true });
      toast.success("Pipeline updated");
      setShowPipelineEditor(false);
      fetchSettings();
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save pipeline");
    } finally {
      setPipelineSaving(false);
    }
  };

  // ---- Template CRUD ------------------------------------------------------
  const saveTemplate = async (tpl) => {
    try {
      if (tpl.template_id) {
        await axios.put(`${API}/leads/templates/${tpl.template_id}`, {
          name: tpl.name, subject: tpl.subject, body: tpl.body,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/leads/templates/`, {
          name: tpl.name, subject: tpl.subject, body: tpl.body,
        }, { withCredentials: true });
      }
      toast.success("Template saved");
      setTplDraft(null);
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/leads/templates/${id}`, { withCredentials: true });
      toast.success("Template deleted");
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete template");
    }
  };

  // Apply a template to the email composer of the currently-open lead.
  const applyTemplate = (template) => {
    if (!editLead) return;
    const varMap = buildVarMap(editLead);
    setEditForm(f => ({
      ...f,
      emailSubject: substituteVars(template.subject || "", varMap),
      emailBody: substituteVars(template.body || "", varMap),
    }));
    toast.success(`Loaded template: ${template.name}`);
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/leads/${editLead.lead_id}`, {
        status: editForm.status,
        assigned_to: editForm.assigned_to,
        notes: editForm.notes,
        estimated_value: editForm.estimated_value,
      }, { withCredentials: true });
      toast.success("Lead updated");
      setEditLead(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async () => {
    if (!editForm.noteBody?.trim()) { toast.error("Note body required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${editLead.lead_id}/notes`, {
        body: editForm.noteBody,
        customer_visible: !!editForm.noteVisible,
        subject: editForm.noteSubject || undefined,
      }, { withCredentials: true });
      toast.success(editForm.noteVisible ? "Note saved & emailed to lead" : "Internal note saved");
      // Reload the lead to reflect new history without closing dialog
      const r = await axios.get(`${API}/leads/${editLead.lead_id}`, { withCredentials: true });
      setEditLead(r.data);
      setEditForm(f => ({ ...f, noteBody: "", noteSubject: "", noteVisible: false }));
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const submitEmail = async () => {
    if (!editForm.emailSubject?.trim() || !editForm.emailBody?.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${editLead.lead_id}/email`, {
        subject: editForm.emailSubject,
        body_html: editForm.emailBody,
      }, { withCredentials: true });
      toast.success(`Email sent to ${editLead.email}`);
      const r = await axios.get(`${API}/leads/${editLead.lead_id}`, { withCredentials: true });
      setEditLead(r.data);
      setEditForm(f => ({ ...f, emailSubject: "", emailBody: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setSaving(false);
    }
  };

  // ---- Drag & drop (Kanban) -------------------------------------------------
  // We use HTML5 dataTransfer to keep this dependency-free. Optimistic update
  // flips the column instantly; on failure we fetch fresh state to roll back.
  const onDragStart = (lead) => (e) => {
    draggingRef.current = lead;
    try { e.dataTransfer.setData("text/plain", lead.lead_id); } catch { /* IE */ }
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (newStatus) => async (e) => {
    e.preventDefault();
    const lead = draggingRef.current;
    draggingRef.current = null;
    if (!lead || lead.status === newStatus) return;
    setLeads(curr => curr.map(l => l.lead_id === lead.lead_id ? { ...l, status: newStatus } : l));
    try {
      await axios.patch(`${API}/leads/${lead.lead_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Moved to ${newStatus}`);
      fetchLeads();
    } catch (err) {
      toast.error("Failed to move lead");
      fetchLeads();
    }
  };

  const labelCls = isDark ? "text-slate-400" : "text-gray-500";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Target className="w-6 h-6 text-violet-500" />
            Leads
          </h1>
          <p className={`mt-1 ${labelCls}`}>Sales pipeline. Drag to advance status. Auto-fed from churn, trial expiry, and the contact form.</p>
        </div>
        <div className="flex gap-2">
          {/* View toggle */}
          <div className={`flex border rounded-md ${isDark ? 'border-slate-700' : 'border-gray-200'}`} data-testid="leads-view-toggle">
            <button
              onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${view === 'kanban' ? 'bg-violet-100 text-violet-700' : (isDark ? 'text-slate-400' : 'text-gray-600')}`}
              data-testid="leads-view-kanban"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
            <button
              onClick={() => setView("table")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${view === 'table' ? 'bg-violet-100 text-violet-700' : (isDark ? 'text-slate-400' : 'text-gray-600')}`}
              data-testid="leads-view-table"
            >
              <List className="w-3.5 h-3.5" /> Table
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setPipelineDraft(statuses.map(s => ({ id: s.id, label: s.label, color: s.color || 'violet', order: s.order ?? 0 }))); setShowPipelineEditor(true); }} data-testid="leads-pipeline-settings-btn">
            <Settings className="w-4 h-4 mr-1" /> Pipeline
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowTemplatesEditor(true)} data-testid="leads-templates-settings-btn">
            <FileText className="w-4 h-4 mr-1" /> Templates
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="leads-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="leads-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New Lead
          </Button>
        </div>
      </div>

      {/* Pipeline KPI strip (always visible) */}
      <div className={`grid grid-cols-2 gap-3 md:grid-cols-${Math.min(statuses.length, 6)}`} data-testid="leads-pipeline-strip">
        {statuses.map(s => (
          <Card
            key={s.id}
            onClick={() => setStatusFilter(statusFilter === s.id ? "all" : s.id)}
            className={`p-4 cursor-pointer transition-all ${statusFilter === s.id ? 'ring-2 ring-violet-500' : ''} ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
            data-testid={`leads-pipeline-${s.id}`}
          >
            <p className={`text-xs ${labelCls}`}>{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{pipeline[s.id] || 0}</p>
            <p className={`text-xs mt-1 ${labelCls}`}>{formatCurrency(pipelineValue[s.id] || 0)}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${labelCls}`} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, company…"
            className="pl-9"
            data-testid="leads-search-input"
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="leads-source-filter">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {Object.keys(SOURCE_LABEL).map(k => (
              <SelectItem key={k} value={k}>{SOURCE_LABEL[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Card className="p-12 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-violet-500" /></Card>
      ) : view === "kanban" ? (
        /* ---- Kanban board ---- */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="leads-kanban">
          {statuses.map(col => {
            const colLeads = visible.filter(l => l.status === col.id);
            const colValue = colLeads.reduce((acc, l) => acc + (Number(l.estimated_value) || 0), 0);
            return (
              <div
                key={col.id}
                onDragOver={onDragOver}
                onDrop={onDrop(col.id)}
                className={`rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50/50'}`}
                data-testid={`leads-column-${col.id}`}
              >
                <div className={`px-3 py-2 border-b text-xs font-semibold flex items-center justify-between rounded-t-lg ${col.headerTone}`}>
                  <span>{col.label} · {colLeads.length}</span>
                  <span className="font-mono">{formatCurrency(colValue)}</span>
                </div>
                <div className="p-2 min-h-[120px] space-y-2">
                  {colLeads.length === 0 && (
                    <p className={`text-xs text-center py-4 ${labelCls}`}>Drop here</p>
                  )}
                  {colLeads.map(l => (
                    <div
                      key={l.lead_id}
                      draggable
                      onDragStart={onDragStart(l)}
                      onClick={() => openEdit(l)}
                      data-testid={`lead-card-${l.lead_id}`}
                      className={`p-3 rounded border cursor-pointer hover:shadow-md transition-shadow ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}
                    >
                      <p className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{l.name || l.email}</p>
                      <p className={`text-xs flex items-center gap-1 truncate ${labelCls}`}><Mail className="w-3 h-3" /> {l.email}</p>
                      {l.company_name && (
                        <p className={`text-xs flex items-center gap-1 truncate ${labelCls}`}><Building2 className="w-3 h-3" /> {l.company_name}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${col.tone}`}>{SOURCE_LABEL[l.source] || l.source}</span>
                        {l.estimated_value > 0 && (
                          <span className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>{formatCurrency(l.estimated_value)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ---- Table view ---- */
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          {visible.length === 0 ? (
            <div className="p-12 text-center">
              <Target className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={labelCls}>No leads match the current filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${labelCls}`}>Lead</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${labelCls}`}>Source</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${labelCls}`}>Status</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${labelCls}`}>Value</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${labelCls}`}>Created</th>
                    <th className={`text-right py-3 px-4 text-sm font-medium ${labelCls}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((l) => {
                    const status = statuses.find(s => s.id === l.status) || statuses[0] || DEFAULT_STATUSES[0];
                    return (
                      <tr
                        key={l.lead_id}
                        className={`border-b cursor-pointer ${isDark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-gray-100 hover:bg-gray-50'}`}
                        onClick={() => openEdit(l)}
                        data-testid={`lead-row-${l.lead_id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold ${isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-700'}`}>
                              {(l.name || l.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{l.name || '—'}</p>
                              <p className={`text-xs flex items-center gap-1 truncate ${labelCls}`}><Mail className="w-3 h-3" /> {l.email}</p>
                              {l.company_name && (<p className={`text-xs flex items-center gap-1 truncate ${labelCls}`}><Building2 className="w-3 h-3" /> {l.company_name}</p>)}
                            </div>
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{SOURCE_LABEL[l.source] || l.source}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${status.tone}`}>{status.label}</span>
                        </td>
                        <td className={`py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{formatCurrency(l.estimated_value)}</td>
                        <td className={`py-3 px-4 text-sm ${labelCls}`}>{formatDate(l.created_at)}</td>
                        <td className="py-3 px-4 text-right"><ChevronRight className={`w-4 h-4 inline ${labelCls}`} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Create dialog (unchanged) */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="lead-create-dialog">
          <DialogHeader>
            <DialogTitle>New Lead</DialogTitle>
            <DialogDescription>Manually add a sales lead to the pipeline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name</Label><Input value={createForm.name} onChange={e => setCreateForm(f => ({...f, name: e.target.value}))} /></div>
            <div><Label>Email *</Label><Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({...f, email: e.target.value}))} /></div>
            <div><Label>Company</Label><Input value={createForm.company_name} onChange={e => setCreateForm(f => ({...f, company_name: e.target.value}))} /></div>
            <div><Label>Phone</Label><Input value={createForm.phone} onChange={e => setCreateForm(f => ({...f, phone: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={createForm.source} onValueChange={(v) => setCreateForm(f => ({...f, source: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(SOURCE_LABEL).map(k => (<SelectItem key={k} value={k}>{SOURCE_LABEL[k]}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estimated value (£/mo)</Label>
                <Input type="number" min="0" step="1" value={createForm.estimated_value} onChange={e => setCreateForm(f => ({...f, estimated_value: Number(e.target.value)}))} />
              </div>
            </div>
            <div><Label>Notes</Label><Textarea rows={3} value={createForm.notes} onChange={e => setCreateForm(f => ({...f, notes: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="lead-create-submit">
              {creating ? "Creating…" : "Create lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / details dialog with tabs (Update / Email / Notes / History) */}
      <Dialog open={!!editLead} onOpenChange={(v) => { if (!v) setEditLead(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-violet-500" />
              {editLead?.name || editLead?.email}
            </DialogTitle>
            <DialogDescription className="space-y-0.5">
              <span className="block text-xs">{editLead?.email}</span>
              {editLead?.company_name && <span className="block text-xs">{editLead.company_name}</span>}
              {editLead?.phone && <span className="block text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {editLead.phone}</span>}
              <span className="block text-xs">Source: <strong>{SOURCE_LABEL[editLead?.source] || editLead?.source}</strong></span>
            </DialogDescription>
          </DialogHeader>

          {/* Update form */}
          <div className="space-y-3 py-2 border-t pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="lead-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map(s => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estimated value (£/mo)</Label>
                <Input type="number" min="0" step="1" value={editForm.estimated_value || 0} onChange={e => setEditForm(f => ({...f, estimated_value: Number(e.target.value)}))} />
              </div>
            </div>
            <div>
              <Label>Assigned to (user id / email)</Label>
              <Input value={editForm.assigned_to || ""} onChange={e => setEditForm(f => ({...f, assigned_to: e.target.value}))} />
            </div>
            <div>
              <Label>Pinned summary notes</Label>
              <Textarea rows={2} value={editForm.notes || ""} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} />
            </div>
            <div className="text-right">
              <Button size="sm" onClick={submitEdit} disabled={saving} data-testid="lead-edit-save">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>

          {/* Send email composer */}
          <div className="space-y-3 py-2 border-t pt-4">
            <div className="flex items-center justify-between">
              <p className={`text-sm font-semibold flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Send className="w-4 h-4 text-violet-500" /> Send email to {editLead?.email}
              </p>
              {/* Template picker — instantly fills the composer with a pre-
                  designed body, with all {{variables}} substituted from the
                  current lead. Empty list (no templates yet) is hidden. */}
              {templates.length > 0 && (
                <Select onValueChange={(tid) => {
                  const t = templates.find(x => x.template_id === tid);
                  if (t) applyTemplate(t);
                }}>
                  <SelectTrigger className="w-56 h-8 text-xs" data-testid="lead-email-template-picker">
                    <SelectValue placeholder="📧 Pick a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Input placeholder="Subject"
              value={editForm.emailSubject || ""}
              onChange={e => setEditForm(f => ({...f, emailSubject: e.target.value}))}
              data-testid="lead-email-subject" />
            <Textarea rows={6} placeholder="Email body (HTML supported)"
              value={editForm.emailBody || ""}
              onChange={e => setEditForm(f => ({...f, emailBody: e.target.value}))}
              data-testid="lead-email-body" />
            {availableVars.length > 0 && (
              <p className="text-[10px] text-gray-500">
                Variables: {availableVars.map(v => <code key={v} className="mx-0.5 px-1 bg-gray-100 rounded">{`{{${v}}}`}</code>)}
              </p>
            )}
            <div className="text-right">
              <Button size="sm" onClick={submitEmail} disabled={saving} data-testid="lead-email-send">
                <Send className="w-4 h-4 mr-1" /> Send email
              </Button>
            </div>
          </div>

          {/* Add note (internal vs customer-visible) */}
          <div className="space-y-3 py-2 border-t pt-4">
            <p className={`text-sm font-semibold flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <MessageSquare className="w-4 h-4 text-violet-500" /> Add note
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setEditForm(f => ({ ...f, noteVisible: false }))}
                className={`px-3 py-2 rounded border text-sm font-medium transition-colors flex items-center gap-2 ${
                  !editForm.noteVisible
                    ? "bg-slate-100 border-slate-400 text-slate-700"
                    : (isDark ? "border-slate-600 text-slate-300" : "border-gray-300 text-gray-700")
                }`}
                data-testid="lead-note-internal"
              >
                <Lock className="w-3 h-3" /> Internal only
              </button>
              <button
                type="button"
                onClick={() => setEditForm(f => ({ ...f, noteVisible: true }))}
                className={`px-3 py-2 rounded border text-sm font-medium transition-colors flex items-center gap-2 ${
                  editForm.noteVisible
                    ? "bg-emerald-50 border-emerald-400 text-emerald-700"
                    : (isDark ? "border-slate-600 text-slate-300" : "border-gray-300 text-gray-700")
                }`}
                data-testid="lead-note-visible"
              >
                <Mail className="w-3 h-3" /> Visible to customer (sends email)
              </button>
            </div>
            {editForm.noteVisible && (
              <Input placeholder="Email subject (optional)"
                value={editForm.noteSubject || ""}
                onChange={e => setEditForm(f => ({...f, noteSubject: e.target.value}))} />
            )}
            <Textarea rows={3} placeholder="Note body"
              value={editForm.noteBody || ""}
              onChange={e => setEditForm(f => ({...f, noteBody: e.target.value}))}
              data-testid="lead-note-body" />
            <div className="text-right">
              <Button size="sm" onClick={submitNote} disabled={saving} data-testid="lead-note-save">
                <Save className="w-4 h-4 mr-1" /> Add note
              </Button>
            </div>
          </div>

          {/* Email & activity history — always shown so internal notes are
              never hidden. Mixes outbound/inbound emails, customer-visible
              notes, internal notes (yellow sticky), and structural events. */}
          <div className="space-y-3 py-2 border-t pt-4">
            <p className={`text-sm font-semibold flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Reply className="w-4 h-4 text-violet-500" /> Activity history
            </p>
            {((editLead?.email_history?.length || 0) + (editLead?.history?.length || 0)) === 0 ? (
              <p className={`text-xs italic ${labelCls}`}>No activity yet.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-2" data-testid="lead-activity-history">
                {(() => {
                  // Build a unified, time-sorted timeline so notes and emails
                  // are interleaved correctly. Internal notes get their own
                  // yellow sticky styling so they're impossible to miss.
                  const items = [];
                  for (const m of editLead.email_history || []) {
                    items.push({ kind: m.direction === 'inbound' ? 'email_in' : 'email_out', ts: m.ts, data: m });
                  }
                  for (const h of editLead.history || []) {
                    if (h.type === 'email_sent' || h.type === 'email_received') continue;
                    if (h.type === 'internal_note')      items.push({ kind: 'internal_note', ts: h.ts, data: h });
                    else if (h.type === 'customer_note') items.push({ kind: 'customer_note', ts: h.ts, data: h });
                    else items.push({ kind: 'event', ts: h.ts, data: h });
                  }
                  items.sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
                  return items.map((it, i) => {
                    if (it.kind === 'internal_note') {
                      return (
                        <div key={`it-${i}`} className="p-2 rounded border border-yellow-300 bg-yellow-50" data-testid="activity-internal-note">
                          <p className="text-xs font-medium text-yellow-800 flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Internal note
                            <span className="text-yellow-700/70">· {(it.data.actor || '').slice(0, 40)} · {formatDateTime(it.ts)}</span>
                          </p>
                          <p className="text-xs whitespace-pre-wrap break-words mt-1 text-yellow-900">{it.data.body}</p>
                        </div>
                      );
                    }
                    if (it.kind === 'customer_note') {
                      return (
                        <div key={`it-${i}`} className="p-2 rounded border border-violet-200 bg-violet-50" data-testid="activity-customer-note">
                          <p className="text-xs font-medium text-violet-800 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Customer-visible note (emailed)
                            <span className="text-violet-700/70">· {(it.data.actor || '').slice(0, 40)} · {formatDateTime(it.ts)}</span>
                          </p>
                          <p className="text-xs whitespace-pre-wrap break-words mt-1 text-violet-900">{it.data.body}</p>
                        </div>
                      );
                    }
                    if (it.kind === 'email_in' || it.kind === 'email_out') {
                      const m = it.data;
                      return (
                        <div key={`it-${i}`} className={`p-2 rounded border ${it.kind === 'email_in' ? 'border-emerald-200 bg-emerald-50/50' : 'border-blue-200 bg-blue-50/50'}`}>
                          <p className="text-xs font-medium flex items-center gap-1">
                            {it.kind === 'email_in' ? <Reply className="w-3 h-3 text-emerald-600" /> : <Send className="w-3 h-3 text-blue-600" />}
                            <span>{it.kind === 'email_in' ? "← Reply from " + (m.from || editLead.email) : "→ Sent to " + (m.to || editLead.email)}</span>
                            <span className={labelCls}>· {formatDateTime(m.ts)}</span>
                            {it.kind === 'email_out' && !m.delivered && (
                              <span className="text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> bounced</span>
                            )}
                          </p>
                          <p className="text-xs font-semibold mt-1">{m.subject}</p>
                          <p className="text-xs whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: (m.body || '').slice(0, 800) }} />
                        </div>
                      );
                    }
                    return (
                      <div key={`it-${i}`} className={`p-2 rounded text-xs ${labelCls}`}>
                        <span className="font-mono">{formatDateTime(it.ts)}</span>{" · "}
                        <span className={`font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{it.data.actor}</span>{" · "}
                        {(it.data.type || '').replace(/_/g, ' ')}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLead(null)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pipeline editor — add/remove/rename columns. Drag-to-reorder via
          handle, plus up/down arrows as a fallback for accessibility. ID
          is auto-derived from label; users can rename freely. */}
      <Dialog open={showPipelineEditor} onOpenChange={setShowPipelineEditor}>
        <DialogContent className="max-w-2xl" data-testid="pipeline-editor-dialog">
          <DialogHeader>
            <DialogTitle>Customise Lead Pipeline</DialogTitle>
            <DialogDescription>Add, remove, rename, recolor, or drag to reorder pipeline columns. Existing leads keep their status — if you rename or remove a status, leads in that column will appear under their old status label until they're moved.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
            {pipelineDraft.map((col, i) => (
              <div
                key={i}
                className="flex gap-2 items-center p-2 border rounded bg-white dark:bg-slate-800 cursor-move"
                draggable
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = Number(e.dataTransfer.getData('text/plain'));
                  const to = i;
                  if (Number.isNaN(from) || from === to) return;
                  setPipelineDraft(d => {
                    const a = [...d];
                    const [moved] = a.splice(from, 1);
                    a.splice(to, 0, moved);
                    return a;
                  });
                }}
                data-testid={`pipeline-col-${i}`}
              >
                <span className="text-gray-400 select-none cursor-grab" title="Drag to reorder">⋮⋮</span>
                <span className="text-xs w-6 text-gray-400 font-mono">{i + 1}</span>
                <Input
                  className="flex-1"
                  placeholder="Label (e.g. Negotiation)"
                  value={col.label}
                  onChange={e => setPipelineDraft(d => d.map((c, idx) => idx === i ? { ...c, label: e.target.value } : c))}
                />
                <Select value={col.color} onValueChange={(v) => setPipelineDraft(d => d.map((c, idx) => idx === i ? { ...c, color: v } : c))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(COLOR_TONES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" disabled={i === 0}
                  onClick={() => setPipelineDraft(d => { const a = [...d]; [a[i-1], a[i]] = [a[i], a[i-1]]; return a; })}>↑</Button>
                <Button variant="ghost" size="sm" disabled={i === pipelineDraft.length - 1}
                  onClick={() => setPipelineDraft(d => { const a = [...d]; [a[i+1], a[i]] = [a[i], a[i+1]]; return a; })}>↓</Button>
                <Button variant="ghost" size="sm" className="text-red-500"
                  onClick={() => setPipelineDraft(d => d.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setPipelineDraft(d => [...d, { id: '', label: '', color: 'violet', order: d.length }])}>
              <Plus className="w-4 h-4 mr-1" /> Add column
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPipelineEditor(false)} disabled={pipelineSaving}>Cancel</Button>
            <Button onClick={savePipeline} disabled={pipelineSaving} data-testid="pipeline-save-btn">
              {pipelineSaving ? "Saving…" : "Save pipeline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates manager — list + edit + create dialog. Default templates
          can be edited but not deleted. Variables `{{var}}` are interpolated
          when the template is applied to a lead. */}
      <Dialog open={showTemplatesEditor} onOpenChange={setShowTemplatesEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="templates-editor-dialog">
          <DialogHeader>
            <DialogTitle>Email Templates</DialogTitle>
            <DialogDescription>
              {tplDraft ? "Edit template" : "Pre-designed email bodies you can apply to any lead. Variables like {{first_name}} are auto-filled."}
            </DialogDescription>
          </DialogHeader>
          {!tplDraft ? (
            <div className="space-y-2 py-2">
              {templates.map(t => (
                <div key={t.template_id} className="flex items-center justify-between p-2 border rounded" data-testid={`tpl-row-${t.template_id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{t.name} {t.is_default && <span className="text-[10px] text-gray-400">(default)</span>}</p>
                    <p className="text-xs text-gray-500 truncate">{t.subject}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setTplDraft({...t})}>Edit</Button>
                    {!t.is_default && (
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteTemplate(t.template_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setTplDraft({ name: '', subject: '', body: '' })}>
                <Plus className="w-4 h-4 mr-1" /> New template
              </Button>
              {availableVars.length > 0 && (
                <p className="text-[10px] text-gray-500 pt-2 border-t">
                  Available variables: {availableVars.map(v => <code key={v} className="mx-0.5 px-1 bg-gray-100 rounded">{`{{${v}}}`}</code>)}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div>
                <Label>Name</Label>
                <Input value={tplDraft.name || ''} onChange={e => setTplDraft(t => ({...t, name: e.target.value}))} />
              </div>
              <div>
                <Label>Subject</Label>
                <Input value={tplDraft.subject || ''} onChange={e => setTplDraft(t => ({...t, subject: e.target.value}))} />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <Textarea rows={10} value={tplDraft.body || ''} onChange={e => setTplDraft(t => ({...t, body: e.target.value}))} />
              </div>
              <p className="text-[10px] text-gray-500">
                Variables: {availableVars.map(v => <code key={v} className="mx-0.5 px-1 bg-gray-100 rounded">{`{{${v}}}`}</code>)}
              </p>
            </div>
          )}
          <DialogFooter>
            {tplDraft ? (
              <>
                <Button variant="outline" onClick={() => setTplDraft(null)}>Back</Button>
                <Button onClick={() => saveTemplate(tplDraft)} data-testid="tpl-save-btn">Save template</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowTemplatesEditor(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
